import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { currency, num } from "@/lib/format";
import { StatusBadge, CategoryBadge, SkuTag } from "@/components/Badges";
import { PageHeader } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, Download, Upload, FileSpreadsheet, CheckCircle2, AlertCircle } from "lucide-react";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { num as fmtNum } from "@/lib/format";

const CAT_FILTERS = [
  { key: "all", label: "All Items" },
  { key: "finished", label: "Finished" },
  { key: "raw", label: "Raw Materials" },
  { key: "packaging", label: "Packaging" },
  { key: "merchandise", label: "Merch" },
];
const HEALTH = [
  { key: "all", label: "All" },
  { key: "in_stock", label: "Healthy" },
  { key: "low_stock", label: "Low" },
  { key: "out_of_stock", label: "Out" },
];

export default function SKUs() {
  const [skus, setSkus] = useState([]);
  const [cat, setCat] = useState("all");
  const [health, setHealth] = useState("all");
  const [q, setQ] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/skus").then((r) => setSkus(r.data));
  useEffect(() => { load(); }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      await load();
      toast.success(`Import complete — ${data.updated_count} quantities updated`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed. Please check your file.");
      setImportOpen(false);
    } finally {
      setImporting(false);
    }
  };

  const filtered = useMemo(() => skus.filter((s) => {
    if (cat !== "all" && s.category !== cat) return false;
    if (health !== "all" && s.status !== health) return false;
    if (q && !(`${s.name} ${s.sku_code} ${s.vendor}`.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  }), [skus, cat, health, q]);

  const totalValue = filtered.reduce((a, s) => a + s.total_value, 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      <PageHeader title="SKUs & Inventory" subtitle={`${filtered.length} items · ${currency(totalValue)} total value`}
        action={
          <div className="flex items-center gap-2">
            <Button data-testid="btn-export-inventory" variant="outline" onClick={() => window.open(`${API}/export`, "_blank")} className="active:scale-[0.98]">
              <Download className="h-4 w-4 mr-1.5" />Export
            </Button>
            <Button data-testid="btn-open-import" onClick={() => { setResult(null); setImportOpen(true); }} className="bg-stone-900 hover:bg-stone-800 active:scale-[0.98]">
              <Upload className="h-4 w-4 mr-1.5" />Import Sheet
            </Button>
          </div>
        } />


      <div className="rounded-lg border border-stone-200 bg-white shadow-sm overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-stone-200 bg-stone-50/50">
          <div className="flex flex-wrap items-center gap-1.5">
            {CAT_FILTERS.map((f) => (
              <button key={f.key} data-testid={`filter-category-${f.key}`} onClick={() => setCat(f.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${cat === f.key ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-600 border-stone-200 hover:border-stone-300"}`}>
                {f.label}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="h-4 w-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="search-skus-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, SKU, vendor…" className="pl-9 w-64 h-9 bg-white" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 px-4 py-2.5 border-b border-stone-200 bg-white">
          <span className="text-xs text-stone-400 mr-1">Stock health:</span>
          {HEALTH.map((h) => (
            <button key={h.key} data-testid={`filter-health-${h.key}`} onClick={() => setHealth(h.key)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${health === h.key ? "bg-stone-200 text-stone-900" : "text-stone-500 hover:bg-stone-100"}`}>
              {h.label}
            </button>
          ))}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs font-semibold uppercase tracking-wider text-stone-500 bg-stone-50/80 border-b border-stone-200">
                <th className="px-4 py-3 text-left">SKU / Item</th>
                <th className="px-4 py-3 text-left">Category</th>
                <th className="px-4 py-3 text-right">On Hand</th>
                <th className="px-4 py-3 text-right">Reorder Pt</th>
                <th className="px-4 py-3 text-right">Unit Cost</th>
                <th className="px-4 py-3 text-right">Asset Value</th>
                <th className="px-4 py-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.sku_code} data-testid={`sku-row-${s.sku_code}`} onClick={() => navigate(`/skus/${s.sku_code}`)}
                  className="border-b border-stone-100 last:border-0 hover:bg-stone-50/70 cursor-pointer transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-stone-800">{s.name}</div>
                    <div className="mt-1 flex items-center gap-2"><SkuTag code={s.sku_code} /><span className="text-xs text-stone-400">{s.vendor}</span></div>
                  </td>
                  <td className="px-4 py-3"><CategoryBadge category={s.category} /></td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-stone-800">{num(s.total_on_hand)} <span className="text-stone-400">{s.unit}</span></td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-stone-500">{num(s.reorder_point)}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-stone-600">{currency(s.unit_cost)}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums font-semibold text-stone-900">{currency(s.total_value)}</td>
                  <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-12 text-stone-400">No items match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={importOpen} onOpenChange={(o) => { if (!importing) setImportOpen(o); }}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-emerald-600" />Import Fun Guy Spreadsheet</DialogTitle>
          </DialogHeader>

          {!result ? (
            <div className="space-y-4 py-1">
              <p className="text-sm text-stone-500 leading-relaxed">
                Upload an updated <span className="font-medium text-stone-700">.xlsx</span> to refresh on-hand quantities.
                The file needs a <span className="font-mono text-xs bg-stone-100 px-1 rounded">Quantity</span> column plus a
                <span className="font-mono text-xs bg-stone-100 px-1 rounded"> SKU Code</span> (or Item Name) and
                <span className="font-mono text-xs bg-stone-100 px-1 rounded"> Facility ID</span> column.
              </p>
              <button data-testid="btn-download-template" onClick={() => window.open(`${API}/export`, "_blank")}
                className="text-sm text-emerald-700 hover:underline inline-flex items-center gap-1.5">
                <Download className="h-3.5 w-3.5" />Download current inventory as a template
              </button>
              <label data-testid="import-dropzone" className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-stone-300 bg-stone-50/50 py-8 cursor-pointer hover:border-stone-400 transition-colors ${importing ? "opacity-60 pointer-events-none" : ""}`}>
                <Upload className="h-6 w-6 text-stone-400" />
                <span className="text-sm font-medium text-stone-700">{importing ? "Importing…" : "Click to choose a .xlsx file"}</span>
                <span className="text-xs text-stone-400">Quantities will update; a movement is logged for each change</span>
                <input data-testid="import-file-input" type="file" accept=".xlsx" className="hidden" onChange={handleFile} disabled={importing} />
              </label>
            </div>
          ) : (
            <div className="space-y-4 py-1" data-testid="import-result">
              <div className="grid grid-cols-4 gap-2 text-center">
                {[["Updated", result.updated_count, "text-emerald-700"], ["Unchanged", result.unchanged_count, "text-stone-600"],
                  ["Skipped", result.skipped_count, "text-stone-500"], ["Unmatched", result.unmatched_count, "text-amber-700"]].map(([l, v, c]) => (
                  <div key={l} className="rounded-lg border border-stone-200 bg-stone-50/60 p-2">
                    <div className={`text-xl font-extrabold font-mono ${c}`}>{v}</div>
                    <div className="text-[11px] uppercase tracking-wide text-stone-400">{l}</div>
                  </div>
                ))}
              </div>
              {result.updated.length > 0 && (
                <div className="max-h-48 overflow-y-auto rounded-lg border border-stone-200">
                  <table className="w-full text-xs">
                    <thead><tr className="text-stone-500 bg-stone-50 border-b border-stone-200"><th className="px-3 py-2 text-left font-semibold">Item</th><th className="px-3 py-2 text-left font-semibold">Facility</th><th className="px-3 py-2 text-right font-semibold">Change</th></tr></thead>
                    <tbody>
                      {result.updated.map((u, i) => (
                        <tr key={i} className="border-b border-stone-50">
                          <td className="px-3 py-1.5"><span className="font-medium text-stone-700">{u.name}</span></td>
                          <td className="px-3 py-1.5 text-stone-500">{u.facility}</td>
                          <td className="px-3 py-1.5 text-right font-mono">{fmtNum(u.old)} → <span className="font-semibold text-stone-800">{fmtNum(u.new)}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {result.unmatched.length > 0 && (
                <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <div><span className="font-semibold">Not matched:</span> {result.unmatched.join(", ")}</div>
                </div>
              )}
              {result.updated_count > 0 && (
                <div className="flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 className="h-4 w-4" />Inventory refreshed.</div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)} disabled={importing}>{result ? "Done" : "Cancel"}</Button>
            {result && <Button data-testid="btn-import-another" onClick={() => setResult(null)} className="bg-stone-900 hover:bg-stone-800">Import Another</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
